import React, { useState, useEffect } from "react";
import CustomModal from "../../components/CustomModal";
import { useCreateProductSheet, useUpdateProductSheet } from "./hooks";
import { s3API, ProductSheet } from "./api";
import { useT } from "../../i18n";

interface ProductSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editProductSheet?: ProductSheet | null;
}

const sheetCategories = [
  "Technical Sheet",
  "User Guide",
  "API Documentation",
  "Installation Guide",
  "Quick Start Guide",
];

const ProductSheetModal: React.FC<ProductSheetModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editProductSheet,
}) => {
  const t = useT();
  const [sheetName, setSheetName] = useState("");
  const [version, setVersion] = useState("");
  const [category, setCategory] = useState("Technical Sheet");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const createProductSheetMutation = useCreateProductSheet();
  const updateProductSheetMutation = useUpdateProductSheet();

  const isEditMode = !!editProductSheet;

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && editProductSheet) {
      setSheetName(editProductSheet.sheetName);
      setVersion(editProductSheet.version);
      setCategory(editProductSheet.category);
      setDescription(editProductSheet.description || "");
    } else if (!isEditMode) {
      resetForm();
    }
  }, [editProductSheet, isEditMode, isOpen]);

  const resetForm = () => {
    setSheetName("");
    setVersion("");
    setCategory("Technical Sheet");
    setDescription("");
    setFile(null);
    setError("");
    setUploading(false);
  };

  const handleSave = async () => {
    setError("");

    // Basic validation
    if (!sheetName || !version || !category) {
      setError(t("common.validation.requiredFields"));
      return;
    }

    // Version validation (basic semver check)
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(version)) {
      setError(t("common.validation.invalidVersion"));
      return;
    }

    try {
      setUploading(file ? true : false);
      let downloadKey, fileSize, fileType;

      // Upload file to S3 if provided (for new files or when replacing existing file)
      if (file) {
        const uploadResponse = await s3API.uploadProductSheetFile(
          file,
          sheetName,
          version
        );
        downloadKey = uploadResponse.data.fileKey;
        fileSize = uploadResponse.data.fileSize;
        fileType = uploadResponse.data.fileType;
      } else if (isEditMode && editProductSheet) {
        // Keep existing file info if no new file is uploaded
        downloadKey = editProductSheet.downloadKey;
        fileSize = editProductSheet.fileSize;
        fileType = editProductSheet.fileType;
      }

      if (isEditMode && editProductSheet) {
        // Update existing product sheet
        await updateProductSheetMutation.mutateAsync({
          id: editProductSheet.id,
          data: {
            sheetName,
            version,
            category,
            description: description || undefined,
            downloadKey,
            fileSize,
            fileType,
          },
        });
      } else {
        // Create new product sheet
        await createProductSheetMutation.mutateAsync({
          sheetName,
          version,
          category,
          description: description || undefined,
          downloadKey,
          fileSize,
          fileType,
        });
      }

      resetForm();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      setError(
        t(
            isEditMode
              ? "productSheets.errors.updateFailed"
              : "productSheets.errors.createFailed"
          )
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <CustomModal open={isOpen} onClose={onClose}>
      <div className="w-[720px] max-w-full h-auto flex flex-col mx-auto">
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-3 pt-6 pb-4 border-b border-gray-200 w-full">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50">
              <i className="fa-solid fa-file-text text-2xl text-blue-500"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditMode
                  ? t("productSheets.modal.editTitle")
                  : t("productSheets.modal.createTitle")}
              </h2>
              <div className="text-sm text-gray-400 font-medium leading-tight">
                {isEditMode
                  ? t("productSheets.modal.editSubtitle")
                  : t("productSheets.modal.createSubtitle")}
              </div>
            </div>
          </div>
          <form
            className="py-6 space-y-4 w-full"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("productSheets.modal.nameLabel")}
                </label>
                <input
                  type="text"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t("productSheets.modal.namePlaceholder")}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("productSheets.modal.versionLabel")}
                </label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t("productSheets.modal.versionPlaceholder")}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("productSheets.modal.categoryLabel")}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              >
                {sheetCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("productSheets.modal.descriptionLabel")}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t("productSheets.modal.descriptionPlaceholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("productSheets.modal.fileLabel")}{" "}
                {isEditMode && t("productSheets.modal.fileOptionalHint")}
              </label>
              <div className="relative">
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  accept=".pdf,.doc,.docx,.txt,.md"
                />
                {file && (
                  <div className="mt-2 text-sm text-gray-600">
                    {t("productSheets.modal.selectedFile", {
                      name: file.name,
                      size: (file.size / (1024 * 1024)).toFixed(1),
                    })}
                  </div>
                )}
                {isEditMode && editProductSheet?.downloadKey && !file && (
                  <div className="mt-2 text-sm text-green-600">
                    <i className="fa-solid fa-check mr-1"></i>
                    {t("common.currentFile", {
                      name: editProductSheet.downloadKey,
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={
                  createProductSheetMutation.isPending ||
                  updateProductSheetMutation.isPending ||
                  uploading
                }
                className="px-4 py-2 text-sm font-medium text-white bg-[#111827] rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {t("common.loading.uploadingFile")}
                  </>
                ) : createProductSheetMutation.isPending ||
                  updateProductSheetMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {isEditMode
                      ? t("common.loading.updating")
                      : t("common.loading.creating")}
                  </>
                ) : (
                  <>
                    <i
                      className={`fa-solid ${
                        isEditMode ? "fa-save" : "fa-upload"
                      } mr-2`}
                    ></i>
                    {isEditMode
                      ? t("productSheets.modal.submitUpdate")
                      : t("productSheets.modal.submitCreate")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CustomModal>
  );
};

export default ProductSheetModal;
